import { useCallback, useEffect, useRef, useState } from "react";

import { startLevelMeter } from "@/lib/audio-level";
import {
  createLevelWindowAggregator,
  type LevelReading,
  type LevelWindow,
  type LevelWindowAggregator,
} from "@/lib/audio-level-windows";

import {
  type AudioGraphTelemetry,
  captureAudioGraphException,
  reportAudioContextResumeOutcome,
  reportAudioContextStateChange,
  reportAudioGraphContextClosed,
} from "./telemetry";

/**
 * How long record start waits for a suspended context to reach "running"
 * before recording the raw stream instead.
 */
export const GRAPH_READY_TIMEOUT_MS = 500;

/** About 46 ms of audio per level read at 44.1 kHz. */
const LEVEL_ANALYSER_FFT_SIZE = 2048;

/** `"none"` means no AudioContext exists, so the raw stream is what records. */
export type AudioGraphContextState = AudioContextState | "none";

export interface AudioGraphReadiness {
  /** True when `recordingStream` is safe to hand to `MediaRecorder`. */
  ready: boolean;
  contextState: AudioGraphContextState;
}

export interface UseAudioGraphOptions extends AudioGraphTelemetry {
  /**
   * When false (e.g. photo mode, or callers that never record), the raw stream
   * is passed through unchanged and no AudioContext is created.
   */
  enabled: boolean;
  /**
   * Every sample, for callers that must react while the student is still
   * speaking. A 10 s window is far too late to tell them we cannot hear them.
   */
  onLevelReading?: (reading: LevelReading) => void;
  /** One aggregate per 10 s window, for the breadcrumb trail. */
  onLevelWindow?: (window: LevelWindow) => void;
}

export interface UseAudioGraphReturn {
  /**
   * Stream to hand to the recorder.
   */
  recordingStream: MediaStream | null;
  /**
   * Synchronously resume a suspended AudioContext and report whether the graph
   * is ready to be recorded right now.
   */
  resumeForRecording: () => AudioGraphReadiness;
  /**
   * Wait up to `GRAPH_READY_TIMEOUT_MS` for the context to reach "running".
   * Call only after `resumeForRecording` reported it was not ready.
   */
  waitUntilRunning: () => Promise<AudioGraphReadiness>;
  /** The context's state right now, for a report written after the fact. */
  contextState: () => AudioGraphContextState;
}

/**
 * Resolves when the context reaches "running", or when `timeoutMs` elapses.
 */
function waitForRunningState(
  context: AudioContext,
  timeoutMs: number
): Promise<void> {
  return new Promise((resolve) => {
    function settle() {
      clearTimeout(timeout);
      context.removeEventListener("statechange", onStateChange);
      resolve();
    }

    function onStateChange() {
      if (context.state === "running") settle();
    }

    const timeout = setTimeout(settle, timeoutMs);
    context.addEventListener("statechange", onStateChange);
  });
}

/**
 * Declarative Web Audio transform between the raw capture stream and the
 * recorder. The recorded audio track (the destination node's track) stays
 * constant while the microphone feeding it can be re-wired underneath, which is
 * what makes gapless mid-recording microphone fallback possible —
 * `MediaRecorder` records the track instances present at `start()` and never
 * sees a swap on the raw stream.
 *
 * It is also where audio energy is measured. Readings are only produced while a
 * microphone is wired through the graph.
 */
export function useAudioGraph(
  rawStream: MediaStream | null,
  {
    enabled,
    captureSurface,
    dialogueId,
    onLevelReading,
    onLevelWindow,
  }: UseAudioGraphOptions
): UseAudioGraphReturn {
  const [recordingStream, setRecordingStream] = useState<MediaStream | null>(
    null
  );
  /**
   * The analyser currently wired into a live graph, or null while nothing is
   * being measured.
   */
  const [meteredAnalyser, setMeteredAnalyser] = useState<AnalyserNode | null>(
    null
  );

  // Read at report time, so the callbacks below stay stable across renders.
  const telemetryRef = useRef<AudioGraphTelemetry>({
    captureSurface,
    dialogueId,
  });
  telemetryRef.current = { captureSurface, dialogueId };

  const onLevelReadingRef = useRef(onLevelReading);
  onLevelReadingRef.current = onLevelReading;

  const onLevelWindowRef = useRef(onLevelWindow);
  onLevelWindowRef.current = onLevelWindow;

  const measureLevels = onLevelWindow != null || onLevelReading != null;

  const contextRef = useRef<AudioContext | null>(null);
  const destinationRef = useRef<MediaStreamAudioDestinationNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const detachStateListenerRef = useRef<(() => void) | null>(null);
  const wiredAudioTrackIdRef = useRef<string | null>(null);
  // Kept across re-wiring, so window numbering does not restart on a mic swap.
  const aggregatorRef = useRef<LevelWindowAggregator | null>(null);

  const detachSource = useCallback(() => {
    sourceRef.current?.disconnect();
    sourceRef.current = null;
    wiredAudioTrackIdRef.current = null;
    setMeteredAnalyser(null);
  }, []);

  const cleanup = useCallback(() => {
    detachSource();

    detachStateListenerRef.current?.();
    detachStateListenerRef.current = null;
    analyserRef.current?.disconnect();
    analyserRef.current = null;
    destinationRef.current?.disconnect();
    destinationRef.current = null;
    const context = contextRef.current;
    contextRef.current = null;

    if (context != null && context.state !== "closed") {
      context
        .close()
        .catch((error: unknown) =>
          captureAudioGraphException(error, telemetryRef.current, context.state)
        );
    }
  }, [detachSource]);

  useEffect(() => {
    const audioTrack = rawStream?.getAudioTracks()[0] ?? null;

    // Disabled (e.g. photo mode): close the context so nothing keeps running
    // while audio routing is not needed. It is recreated lazily on re-enable.
    if (!enabled) {
      cleanup();
      setRecordingStream(rawStream);
      return;
    }

    // In case context was closed by the browser
    if (contextRef.current?.state === "closed") cleanup();

    // Transient pass-through while enabled (stream restart in flight, or no
    // audio to route): keep the context warm to avoid churn on every swap.
    if (rawStream == null || audioTrack == null) {
      detachSource();
      setRecordingStream(rawStream);
      return;
    }

    // No Web Audio support: record the raw stream directly (no gapless swap).
    if (window.AudioContext == null) {
      setRecordingStream(rawStream);
      return;
    }

    if (contextRef.current == null) {
      const created = new window.AudioContext();
      contextRef.current = created;
      destinationRef.current = created.createMediaStreamDestination();
      const onStateChange = () =>
        reportAudioContextStateChange(telemetryRef.current, created.state);
      created.addEventListener("statechange", onStateChange);
      detachStateListenerRef.current = () =>
        created.removeEventListener("statechange", onStateChange);
    }
    const context = contextRef.current;
    const destination = destinationRef.current;
    if (destination == null) return;

    if (measureLevels && analyserRef.current == null) {
      const created = context.createAnalyser();
      created.fftSize = LEVEL_ANALYSER_FFT_SIZE;
      analyserRef.current = created;
      // Route the microphone through the new analyser below, rather than leaving
      // it connected straight to the destination: Web Audio only processes nodes
      // the graph pulls, and an unconnected analyser hands out zeros — which
      // read as digital silence from a working microphone.
      wiredAudioTrackIdRef.current = null;
    }
    const analyser = analyserRef.current;

    // Re-wire the source whenever the microphone track changes (recovery), so
    // the constant destination track keeps producing audio across the swap.
    if (wiredAudioTrackIdRef.current !== audioTrack.id) {
      sourceRef.current?.disconnect();
      sourceRef.current = context.createMediaStreamSource(
        new MediaStream([audioTrack])
      );
      if (analyser == null) {
        sourceRef.current.connect(destination);
      } else {
        // Measure destination audio
        sourceRef.current.connect(analyser);
        analyser.connect(destination);
      }
      wiredAudioTrackIdRef.current = audioTrack.id;
      setMeteredAnalyser(analyser);
    }

    // Rebuild the recording stream from the current video tracks plus the
    // constant destination audio track in case the raw stream was swapped out.
    const destinationTrack = destination.stream.getAudioTracks()[0];
    if (destinationTrack != null) {
      setRecordingStream(
        new MediaStream([...rawStream.getVideoTracks(), destinationTrack])
      );
    }
  }, [rawStream, enabled, measureLevels, detachSource, cleanup]);

  // Sampling runs from the moment a microphone is wired
  useEffect(() => {
    if (meteredAnalyser == null) return;

    const aggregator = (aggregatorRef.current ??=
      createLevelWindowAggregator());

    return startLevelMeter(
      meteredAnalyser,
      ({ level }) => {
        const reading: LevelReading = {
          level,
          contextState: meteredAnalyser.context.state,
        };
        onLevelReadingRef.current?.(reading);
        const window = aggregator.push(reading, Date.now());
        if (window != null) onLevelWindowRef.current?.(window);
      },
      () => reportAudioGraphContextClosed(telemetryRef.current)
    );
  }, [meteredAnalyser]);

  useEffect(() => {
    return () => cleanup();
  }, [cleanup]);

  const readiness = useCallback((): AudioGraphReadiness => {
    const context = contextRef.current;
    // Photo mode, or no Web Audio support.
    if (context == null) return { ready: true, contextState: "none" };
    // The context is kept warm across a mic loss, but nothing is wired through
    // it — `recordingStream` is the raw stream, so its state does not matter.
    // Without this, a lost microphone would be reported as a graph failure.
    if (wiredAudioTrackIdRef.current == null) {
      return { ready: true, contextState: context.state };
    }
    return { ready: context.state === "running", contextState: context.state };
  }, []);

  const resumeForRecording = useCallback((): AudioGraphReadiness => {
    const context = contextRef.current;
    if (context?.state === "suspended") {
      context.resume().then(
        () => {
          reportAudioContextResumeOutcome(
            telemetryRef.current,
            "resolved",
            context.state
          );
        },
        (error: unknown) => {
          reportAudioContextResumeOutcome(
            telemetryRef.current,
            "rejected",
            context.state
          );
          captureAudioGraphException(
            error,
            telemetryRef.current,
            context.state
          );
        }
      );
    }
    return readiness();
  }, [readiness]);

  const waitUntilRunning =
    useCallback(async (): Promise<AudioGraphReadiness> => {
      const context = contextRef.current;
      // Only a suspended context can still reach "running". A closed one never
      // will, so waiting on it is pure delay at record start.
      if (!readiness().ready && context?.state === "suspended") {
        await waitForRunningState(context, GRAPH_READY_TIMEOUT_MS);
      }
      return readiness();
    }, [readiness]);

  const contextState = useCallback(
    (): AudioGraphContextState => contextRef.current?.state ?? "none",
    []
  );

  return {
    recordingStream,
    resumeForRecording,
    waitUntilRunning,
    contextState,
  };
}
